/**
 * Plan Validation — Validate action plans before returning to the caller.
 *
 * Checks that all actionIds exist in the registry, required parameters are
 * present, type references are compatible, and preconditions are satisfiable.
 *
 * @module gibbs/text-to-action/validatePlan
 */

import { getAction, checkPreconditions, isTypeCompatible } from '../registry/index';

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} valid
 * @property {string[]} errors
 */

/**
 * Validate a complete action plan.
 *
 * Checks:
 * 1. All actionIds exist in the registry
 * 2. All required params are present (against ActionDef.inputs and .parameters)
 * 3. Type compatibility of inputsFrom references
 * 4. Preconditions are satisfiable given workspace state + plan steps
 *
 * @param {import('./planActions.js').ActionPlan} plan - The action plan to validate
 * @param {Object} [workspaceState] - Current workspace state for precondition checks
 * @param {{deferredRequiredInputs?: Object<string, string[]>}} [options]
 *   Required inputs intentionally collected by a downstream review surface.
 * @returns {ValidationResult}
 */
export function validatePlan(plan, workspaceState, options = {}) {
    const errors = [];

    if (!plan || !Array.isArray(plan.actions)) {
        errors.push('Plan is missing or has no actions array.');
        return { valid: false, errors };
    }

    if (plan.actions.length === 0 && (plan.intent === 'single_action' || plan.intent === 'workflow')) {
        errors.push(`Plan intent is "${plan.intent}" but actions array is empty.`);
        return { valid: false, errors };
    }

    // Build a map of step outputs for inputsFrom validation
    const stepOutputs = new Map(); // step number -> [{name, type}]

    for (let i = 0; i < plan.actions.length; i++) {
        const action = plan.actions[i];
        const stepLabel = action.step != null ? `step ${action.step}` : `action[${i}]`;

        // 1. Check actionId exists in registry
        const actionDef = validateActionExists(action.actionId, stepLabel, errors);
        if (!actionDef) continue;

        // Track outputs for this step
        if (action.step != null) {
            stepOutputs.set(action.step, actionDef.outputs);
        }

        // 2. Check required parameters
        validateRequiredParams(
            actionDef,
            action,
            stepLabel,
            errors,
            new Set(options.deferredRequiredInputs?.[action.actionId] || []),
        );

        // 3. Check inputsFrom type compatibility
        validateInputsFrom(actionDef, action, stepLabel, stepOutputs, errors);

        // 4. Check preconditions (only if workspace state provided)
        if (workspaceState) {
            validatePreconditions(
                actionDef, action, stepLabel, i,
                plan.actions, stepOutputs, workspaceState, errors
            );
        }
    }

    return { valid: errors.length === 0, errors };
}

/**
 * Quick check: does a plan have any actions with unknown actionIds?
 *
 * @param {import('./planActions.js').ActionPlan} plan
 * @returns {string[]} Unknown actionIds
 */
export function findUnknownActions(plan) {
    if (!plan || !Array.isArray(plan.actions)) return [];
    return plan.actions
        .filter((a) => !getAction(a.actionId))
        .map((a) => a.actionId);
}

/**
 * Check which required params are missing from an action.
 *
 * @param {string} actionId
 * @param {Object} params - Provided parameters
 * @returns {string[]} Names of missing required parameters
 */
export function findMissingParams(actionId, params = {}) {
    const actionDef = getAction(actionId);
    if (!actionDef) return [];

    const missing = [];
    for (const input of actionDef.inputs) {
        if (input.required !== false && !(input.name in params)) {
            missing.push(input.name);
        }
    }
    for (const param of actionDef.parameters) {
        if (param.required && !(param.name in params) && param.defaultValue === undefined) {
            missing.push(param.name);
        }
    }
    return missing;
}

// ─── Internal Validation Helpers ────────────────────────────────────────────

/**
 * Validate that an actionId exists in the registry.
 * @returns {import('../registry/schema.js').ActionDef | null}
 */
function validateActionExists(actionId, stepLabel, errors) {
    if (!actionId) {
        errors.push(`${stepLabel}: missing actionId.`);
        return null;
    }
    const actionDef = getAction(actionId);
    if (!actionDef) {
        errors.push(`${stepLabel}: unknown actionId "${actionId}". Action not found in registry.`);
        return null;
    }
    if (actionDef.status === 'planned') {
        errors.push(`${stepLabel}: action "${actionId}" has status "planned" and is not yet available.`);
    }
    return actionDef;
}

/**
 * Validate that all required inputs and parameters are present.
 * Inputs may be satisfied by resolvedInputs or inputsFrom.
 */
function validateRequiredParams(
    actionDef, action, stepLabel, errors, deferredRequiredInputs = new Set(),
) {
    const allProvided = {
        ...(action.params || {}),
        ...(action.resolvedInputs || {}),
    };
    const inputsFromKeys = action.inputsFrom ? Object.keys(action.inputsFrom) : [];

    // Check required inputs
    for (const input of actionDef.inputs) {
        if (input.required === false) continue;
        const isSatisfied = (input.name in allProvided) || inputsFromKeys.includes(input.name);
        if (!isSatisfied && !deferredRequiredInputs.has(input.name)) {
            errors.push(`${stepLabel} ("${action.actionId}"): missing required input "${input.name}" (type: ${input.type}).`);
        }
    }

    // Check required parameters (those without defaults)
    for (const param of actionDef.parameters) {
        if (!param.required) continue;
        if (param.defaultValue !== undefined) continue;
        if (!(param.name in (action.params || {}))) {
            errors.push(`${stepLabel} ("${action.actionId}"): missing required parameter "${param.name}".`);
        }
    }

    // Validate parameter values against options/constraints
    if (action.params) {
        for (const [name, value] of Object.entries(action.params)) {
            const paramDef = actionDef.parameters.find((p) => p.name === name);
            if (!paramDef) continue;

            if (paramDef.options && !paramDef.options.includes(value)) {
                errors.push(
                    `${stepLabel} ("${action.actionId}"): parameter "${name}" value "${value}" is not one of [${paramDef.options.join(', ')}].`
                );
            }

            if (paramDef.constraints) {
                if (paramDef.constraints.min != null && value < paramDef.constraints.min) {
                    errors.push(`${stepLabel} ("${action.actionId}"): parameter "${name}" value ${value} is below minimum ${paramDef.constraints.min}.`);
                }
                if (paramDef.constraints.max != null && value > paramDef.constraints.max) {
                    errors.push(`${stepLabel} ("${action.actionId}"): parameter "${name}" value ${value} exceeds maximum ${paramDef.constraints.max}.`);
                }
            }
        }
    }
}

/**
 * Validate inputsFrom references: the referenced step must exist and produce
 * a type-compatible output for the consuming input.
 */
function validateInputsFrom(actionDef, action, stepLabel, stepOutputs, errors) {
    if (!action.inputsFrom) return;

    for (const [inputName, ref] of Object.entries(action.inputsFrom)) {
        // Validate ref format: "step:N.outputName"
        const match = typeof ref === 'string' ? ref.match(/^step:(\d+)\.(.+)$/) : null;
        if (!match) {
            errors.push(`${stepLabel} ("${action.actionId}"): inputsFrom "${inputName}" has invalid reference "${ref}". Expected format "step:N.outputName".`);
            continue;
        }

        const refStep = parseInt(match[1], 10);
        const refOutputName = match[2];

        // Check that the referenced step exists and comes before this one
        if (action.step != null && refStep >= action.step) {
            errors.push(`${stepLabel} ("${action.actionId}"): inputsFrom "${inputName}" references step ${refStep} which does not precede step ${action.step}.`);
            continue;
        }

        // Check that the referenced step has the named output
        const refOutputs = stepOutputs.get(refStep);
        if (!refOutputs) {
            errors.push(`${stepLabel} ("${action.actionId}"): inputsFrom "${inputName}" references step ${refStep} which has no registered outputs.`);
            continue;
        }

        const refOutput = refOutputs.find((o) => o.name === refOutputName);
        if (!refOutput) {
            const available = refOutputs.map((o) => o.name).join(', ');
            errors.push(`${stepLabel} ("${action.actionId}"): inputsFrom "${inputName}" references output "${refOutputName}" from step ${refStep}, but that step only produces [${available}].`);
            continue;
        }

        // Check type compatibility
        const inputDef = actionDef.inputs.find((i) => i.name === inputName);
        if (inputDef && !isTypeCompatible(refOutput.type, inputDef.type)) {
            errors.push(`${stepLabel} ("${action.actionId}"): inputsFrom "${inputName}" type mismatch — step ${refStep} output "${refOutputName}" is type "${refOutput.type}" but input "${inputName}" expects type "${inputDef.type}".`);
        }
    }
}

/**
 * Validate preconditions are satisfiable.
 *
 * A precondition is satisfiable if:
 * - It's already met in the current workspace state, OR
 * - A prior step in the plan has a postcondition that guarantees it
 */
function validatePreconditions(
    actionDef, action, stepLabel, actionIndex,
    allActions, stepOutputs, workspaceState, errors
) {
    const { met, unmet } = checkPreconditions(action.actionId, workspaceState);
    if (met) return;

    // Check if prior steps in the plan satisfy unmet preconditions
    const priorPostconditions = new Set();
    for (let j = 0; j < actionIndex; j++) {
        const priorDef = getAction(allActions[j].actionId);
        if (priorDef) {
            for (const post of priorDef.postconditions) {
                priorPostconditions.add(post.guarantee);
            }
        }
    }

    // Map common preconditions to postconditions that satisfy them
    const preconditionToPostcondition = {
        protein_loaded: 'protein_loaded',
        compound_loaded: 'compound_loaded',
        compound_has_3d: 'compound_has_3d',
        fragment_map_available: 'fragment_map_available',
        compound_has_energies: 'compound_has_energies',
    };

    for (const precondId of unmet) {
        // Skip error-formatted unmet entries (from checkPreconditions error handling)
        if (precondId.includes('(error:')) continue;

        const matchingPost = preconditionToPostcondition[precondId] || precondId;
        if (!priorPostconditions.has(matchingPost)) {
            errors.push(`${stepLabel} ("${action.actionId}"): precondition "${precondId}" is not met and no prior step satisfies it.`);
        }
    }
}
